import {
  closestCenter,
  type Announcements,
  type ClientRect,
  type CollisionDetection,
  type MeasuringConfiguration,
  type Modifier,
  type UniqueIdentifier,
} from '@dnd-kit/core'

/** Sidebar drags reorder vertically, so the row never drifts sideways. */
export const restrictDragToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 })

/**
 * Marks the sidebar's scrolling list area. A dragged row stays within the part
 * of its list showing there.
 */
const SIDEBAR_LIST_VIEWPORT_ATTRIBUTE = 'data-sidebar-list-viewport'

/** The scrolling list area around `element`, or `element` itself outside a drawer. */
export function sidebarListViewport(element: Element | null): Element | null {
  return element?.closest(`[${SIDEBAR_LIST_VIEWPORT_ATTRIBUTE}]`) ?? element
}

/**
 * Holds a dragged row between its list's first and last places (and within
 * the part of the list showing), as the Fields dialog does: dragged past the
 * top or bottom, the row stops there, where letting go puts it.
 */
export function restrictDragToList(getList: () => Element | null): Modifier {
  return ({ activeNodeRect, draggingNodeRect, transform }) => {
    const dragRect = draggingNodeRect ?? activeNodeRect
    const list = getList()
    if (!dragRect || !list) return transform

    const listRect = list.getBoundingClientRect()
    const viewportRect = sidebarListViewport(list)?.getBoundingClientRect() ?? listRect
    const minY = Math.max(listRect.top, viewportRect.top) - dragRect.top
    const maxY = Math.max(minY, Math.min(listRect.bottom, viewportRect.bottom) - dragRect.bottom)
    return { ...transform, y: Math.max(minY, Math.min(maxY, transform.y)) }
  }
}

/** How far a computed `transform` moves an element (a translate; no scale). */
function translateOf(element: Element | null): { x: number; y: number } {
  const transform = element ? getComputedStyle(element).transform : 'none'
  const values = /^matrix(3d)?\((.+)\)$/.exec(transform)?.[2].split(',').map(Number)
  if (!values) return { x: 0, y: 0 }
  return values.length === 16 ? { x: values[12], y: values[13] } : { x: values[4], y: values[5] }
}

/**
 * Where the dragged row's chip starts, as dnd-kit places it (DragOverlay).
 * dnd-kit measures the chip once, when it first draws, including how far the
 * pointer has moved by then; a quick drag drew it a few moves in, so
 * restrictDragToList held the row that far off the list's ends. Measured
 * without the drag's own move, the row stops exactly on the first and last
 * places.
 */
export function measureDragChip(node: HTMLElement): ClientRect {
  const rect = node.getBoundingClientRect()
  const own = translateOf(node)
  const overlay = translateOf(node.parentElement)
  const x = own.x + overlay.x
  const y = own.y + overlay.y
  return {
    top: rect.top - y,
    bottom: rect.bottom - y,
    left: rect.left - x,
    right: rect.right - x,
    width: rect.width,
    height: rect.height,
  }
}

/** A sidebar list's drag measures its chip with measureDragChip. */
export const SIDEBAR_DRAG_MEASURING: MeasuringConfiguration = {
  dragOverlay: { measure: measureDragChip },
}

/** A sidebar list's drag: up and down only, never past its first or last place. */
export function sidebarListDragModifiers(getList: () => Element | null): Modifier[] {
  return [restrictDragToVerticalAxis, restrictDragToList(getList)]
}

/**
 * Where a dragged row lands: the row or folder under its chip, wherever the
 * pointer is. The chip stops at the list's ends (restrictDragToList) while the
 * pointer goes on, so a pointer past them counts as at the chip's nearest
 * edge, and `detect` reads it there. Keyboard drags have no pointer.
 */
export function listDragCollision(detect: CollisionDetection = closestCenter): CollisionDetection {
  return (args) => {
    const pointer = args.pointerCoordinates
    const chip = args.collisionRect
    if (!pointer) return detect(args)
    const y = Math.max(chip.top, Math.min(chip.bottom, pointer.y))
    return detect(y === pointer.y ? args : { ...args, pointerCoordinates: { ...pointer, y } })
  }
}

/** Screen-reader announcements that name rows instead of reading internal IDs. */
export function sidebarDragAnnouncements(
  describe: (id: UniqueIdentifier) => string
): Announcements {
  return {
    onDragStart: ({ active }) => `Picked up ${describe(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${describe(active.id)} is over ${describe(over.id)}.`
        : `${describe(active.id)} is outside the list. Let go to put it back.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${describe(active.id)} was dropped on ${describe(over.id)}.`
        : `${describe(active.id)} was put back.`,
    onDragCancel: ({ active }) => `Moving ${describe(active.id)} was cancelled.`,
  }
}
